import { useCallback, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectJobByCourse } from './jobsSlice'
import { studioApi, useCancelJobMutation } from './studioApi'
import { parseStartError } from './lib/parseStartError'
import { courseLanguageFailureText } from './lib/courseLanguageFailureText'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

/** Код отказа читаем сами: у `409` отмены нет `job_id`, и `parseStartError`
 *  уводит его в `unknown`, теряя `job_already_finished`. */
const codeOf = (error: unknown): string | undefined =>
  (error as { data?: { detail?: { code?: string } } } | undefined)?.data?.detail?.code

/**
 * Отмена прогона перевода из меню языка (§6.7). Отменяет ту джобу, на которую
 * сейчас смотрит реестр курса, — перевод или картиночную половину цепочки.
 *
 * `409 job_already_finished` на переводе — гонка «кнопку показали, текст
 * успел закончиться»: бэк уже мог поставить картинки (`result.next_job_id`).
 * Автор подтвердил «Отменить перевод», и очередь обязана остановиться целиком
 * (спека: «очередь оставшихся языков останавливается»), поэтому хвост
 * цепочки отменяем тут же, без второго вопроса.
 */
export const useCancelTranslationRun = (courseId: number) => {
  const dispatch = useAppDispatch()
  const entry = useAppSelector((s) => selectJobByCourse(s, courseId))
  const [cancelJob, { isLoading }] = useCancelJobMutation()
  const [failure, setFailure] = useState<string | null>(null)

  const cancelChainTail = useCallback(
    async (jobId: number): Promise<void> => {
      const fresh = await dispatch(
        studioApi.endpoints.getStudioJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
      ).unwrap()
      const next = fresh.type === 'course_translation' ? fresh.result?.next_job_id : undefined
      if (next) await cancelJob(next).unwrap()
    },
    [dispatch, cancelJob],
  )

  /** Текст отказа или `null`, если отменять больше нечего. Текст возвращается,
   *  а не только кладётся в стейт: отмену подтверждают в диалоге при закрытом
   *  меню, и вызывающему нужно показать отказ тостом сразу, не дожидаясь
   *  перерисовки. */
  const cancel = useCallback(async (): Promise<string | null> => {
    if (!entry) return null
    setFailure(null)
    try {
      await cancelJob(entry.jobId).unwrap()
      return null
    } catch (error) {
      try {
        if (codeOf(error) !== 'job_already_finished') throw error
        await cancelChainTail(entry.jobId)
        return null
      } catch (final) {
        const text = courseLanguageFailureText(
          parseStartError(final),
          messagesNow(messages).cancelTranslationFailed,
        )
        setFailure(text)
        return text
      }
    }
  }, [entry, cancelJob, cancelChainTail])

  return { cancel, isCancelling: isLoading, failure }
}
